import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectTrigger,
    SelectValue,
    SelectContent,
    SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Trophy as Whistle, UserPlus } from "@phosphor-icons/react";

export default function Register() {
    const { refresh } = useAuth();
    const { settings } = useSettings();
    const nav = useNavigate();
    const [form, setForm] = useState({
        name: "",
        email: "",
        password: "",
        confirm: "",
        role: "parent",
    });
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        if (form.password !== form.confirm) {
            setErr("Le password non coincidono");
            return;
        }
        if (form.password.length < 6) {
            setErr("La password deve essere di almeno 6 caratteri");
            return;
        }
        setLoading(true);
        try {
            const { data } = await api.post("/auth/register", {
                name: form.name.trim(),
                email: form.email.trim(),
                password: form.password,
                role: form.role,
            });
            localStorage.setItem("cc_token", data.token);
            await refresh();
            toast.success("Registrazione completata! Benvenuto.");
            nav("/", { replace: true });
        } catch (e2) {
            setErr(formatApiError(e2));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-dvh grid md:grid-cols-2 bg-background">
            <div className="hidden md:flex relative overflow-hidden bg-card border-r border-border/60">
                <img
                    src="https://images.pexels.com/photos/28827841/pexels-photo-28827841.jpeg"
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover opacity-40"
                />
                <div className="absolute inset-0 bg-gradient-to-br from-background via-background/60 to-transparent" />
                <div className="relative z-10 flex flex-col justify-between p-10 w-full">
                    <div className="flex items-center gap-3">
                        <div className="w-11 h-11 rounded-xl grid place-items-center bg-primary/20 text-primary hairline">
                            <Whistle size={24} weight="fill" />
                        </div>
                        <div>
                            <div className="font-display text-xl font-bold tracking-tight uppercase">
                                {settings?.club_name || "Calcio Club"}
                            </div>
                            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                                Registrazione
                            </div>
                        </div>
                    </div>
                    <div>
                        <h1 className="font-display text-5xl lg:text-6xl font-black uppercase leading-[0.9] tracking-tighter">
                            Entra a<br />
                            <span className="text-primary">far parte</span><br />
                            della squadra.
                        </h1>
                        <p className="text-muted-foreground mt-6 max-w-md leading-relaxed">
                            Crea il tuo account gratuito e resta sempre aggiornato su partite,
                            convocazioni e comunicazioni della società.
                        </p>
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                        Stagione 2025 / 2026
                    </div>
                </div>
            </div>

            <div className="flex items-center justify-center px-5 py-10">
                <div className="w-full max-w-sm">
                    <div className="md:hidden flex items-center gap-3 mb-8">
                        <div className="w-11 h-11 rounded-xl grid place-items-center bg-primary/20 text-primary hairline">
                            <Whistle size={24} weight="fill" />
                        </div>
                        <div>
                            <div className="font-display text-xl font-bold tracking-tight uppercase">
                                {settings?.club_name || "Calcio Club"}
                            </div>
                            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                                Registrazione
                            </div>
                        </div>
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                        Nuovo account
                    </div>
                    <h2 className="font-display text-3xl md:text-4xl font-black uppercase tracking-tight mt-1">
                        Crea il tuo profilo
                    </h2>

                    <form onSubmit={submit} className="mt-8 space-y-4">
                        <div className="space-y-2">
                            <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                                Nome e cognome
                            </Label>
                            <Input
                                data-testid="register-name-input"
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                placeholder="Mario Rossi"
                                required
                                className="h-12 bg-card border-border/60"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                                Email
                            </Label>
                            <Input
                                data-testid="register-email-input"
                                type="email"
                                value={form.email}
                                onChange={(e) => setForm({ ...form, email: e.target.value })}
                                placeholder="tu@esempio.it"
                                required
                                className="h-12 bg-card border-border/60"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                                Ruolo
                            </Label>
                            <Select
                                value={form.role}
                                onValueChange={(v) => setForm({ ...form, role: v })}
                            >
                                <SelectTrigger data-testid="register-role-select" className="h-12 bg-card border-border/60">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="parent">Genitore</SelectItem>
                                    <SelectItem value="player">Giocatore / Giocatrice</SelectItem>
                                </SelectContent>
                            </Select>
                            <div className="text-[11px] text-muted-foreground">
                                Gli account di allenatore e società vengono creati dall&apos;amministrazione.
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-2">
                                <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                                    Password
                                </Label>
                                <Input
                                    data-testid="register-password-input"
                                    type="password"
                                    value={form.password}
                                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                                    required
                                    className="h-12 bg-card border-border/60"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                                    Conferma
                                </Label>
                                <Input
                                    data-testid="register-confirm-input"
                                    type="password"
                                    value={form.confirm}
                                    onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                                    required
                                    className="h-12 bg-card border-border/60"
                                />
                            </div>
                        </div>
                        {err && (
                            <div
                                data-testid="register-error"
                                className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-lg px-3 py-2"
                            >
                                {err}
                            </div>
                        )}
                        <Button
                            data-testid="register-submit"
                            type="submit"
                            disabled={loading}
                            className="w-full h-12 text-sm font-semibold uppercase tracking-widest press"
                        >
                            <UserPlus size={16} weight="bold" className="mr-2" />
                            {loading ? "Attendere…" : "Crea account"}
                        </Button>
                    </form>

                    <div className="mt-6 text-sm text-muted-foreground text-center">
                        Hai già un account?{" "}
                        <Link to="/login" className="text-primary press">
                            Accedi
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
}
